'use client';

import React, { useState, useEffect } from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';
import AppImage from '@/components/ui/AppImage';
import { formatDate } from '@/utils/dateUtils';

interface Event {
  id: number;
  title: string;
  date: string;
  location: string;
  description: string;
  image: string;
  alt: string;
  category: string;
  status: 'upcoming' | 'ongoing' | 'completed';
}

const EventsCarousel = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);
  const [events, setEvents] = useState<Event[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    setIsHydrated(true);
    
    const fetchEvents = async () => {
      try {
        const { createClient } = await import('@/utils/supabase/client');
        const supabase = createClient();
        
        const { data, error } = await supabase
          .from('events')
          .select('*')
          .order('date', { ascending: true }); // Get all events ordered by date
          
        if (error) throw error;
        
        if (data) {
          // Process and filter to get only relevant events for carousel (e.g. upcoming/ongoing)
          const now = new Date();
          const processedEvents: Event[] = data.map((e: any) => {
            const eventDate = new Date(e.date);
            let status: 'upcoming' | 'ongoing' | 'completed' = 'upcoming';
            
            if (e.registration_status === 'closed') status = 'completed';
            else if (eventDate < now) status = 'completed';
            else if (e.registration_status === 'full') status = 'ongoing';
            
            let firstImage = e.image;
            if (firstImage) {
              try {
                const parsed = JSON.parse(firstImage);
                if (Array.isArray(parsed)) firstImage = parsed[0];
              } catch {}
            }

            return {
              id: e.id,
              title: e.title || '',
              date: e.date || '',
              location: e.location || '',
              description: e.description || '',
              image: firstImage || 'https://images.unsplash.com/photo-1687180948580-c4892a9a82c8',
              alt: e.title || 'Event image',
              category: e.category || 'Event',
              status
            };
          });
          
          // Filter out completed ones if we want to show only featured/upcoming, or take top 5
          const displayEvents = processedEvents.filter(e => e.status !== 'completed').slice(0, 5);
          
          // Fallback if no upcoming events
          setEvents(displayEvents.length > 0 ? displayEvents : processedEvents.slice(0, 5));
        }
      } catch (error) {
        console.error('Error fetching carousel events:', error);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!isHydrated || events.length === 0) return;

    const timer = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % events.length);
    }, 5000);

    return () => clearInterval(timer);
  }, [isHydrated, events.length]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev - 1 + events.length) % events.length);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev + 1) % events.length);
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchStart(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe) {
      handleNext();
    }
    if (isRightSwipe) {
      handlePrevious();
    }

    setTouchStart(0);
    setTouchEnd(0);
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming':
        return 'bg-primary text-primary-foreground';
      case 'ongoing':
        return 'bg-secondary text-secondary-foreground';
      case 'completed':
        return 'bg-muted text-muted-foreground';
      default:
        return 'bg-muted text-muted-foreground';
    }
  };

  const formatDateValue = (dateString: string) => {
    return formatDate(dateString);
  };

  if (!isHydrated || isLoading) {
    return (
      <section className="py-12 md:py-20 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16">
            <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
              Featured Events & Activities
            </h2>
            <p className="text-lg text-muted-foreground">
              Join us in making a difference through community events and service programs
            </p>
          </div>
          
          <div className="relative max-w-6xl mx-auto">
            <div className="glass-card rounded-3xl overflow-hidden shadow-glass h-[400px] flex items-center justify-center animate-pulse">
              <Icon name="CalendarDaysIcon" size={48} className="text-muted-foreground/30 animate-pulse" />
            </div>
          </div>
        </div>
      </section>);
  }

  if (events.length === 0) {
    return null; // Don't render if no events exist in DB
  }

  return (
    <section className="py-12 md:py-20 bg-muted/30">
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-8 md:mb-16">
          <h2 className="text-2xl md:text-4xl lg:text-5xl font-heading font-bold text-foreground mb-4">
            Featured Events & Activities
          </h2>
          <p className="text-lg text-muted-foreground">
            Join us in making a difference through community events and service programs
          </p>
        </div>

        <div className="relative max-w-6xl mx-auto">
          <div
            className="overflow-hidden rounded-3xl"
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove}
            onTouchEnd={handleTouchEnd}>

            <div
              className="flex transition-transform duration-500 ease-out"
              style={{ transform: `translateX(-${currentIndex * 100}%)` }}>

              {events.map((event) =>
              <div key={event.id} className="w-full flex-shrink-0">
                  <div className="glass-card rounded-3xl overflow-hidden shadow-glass">
                    <div className="grid md:grid-cols-2 gap-0">
                      <div className="relative h-52 sm:h-64 md:h-auto overflow-hidden">
                        <AppImage
                        key={event.id}
                        src={event.image}
                        alt={event.alt}
                        className="w-full h-full object-cover" />

                        <div className="absolute top-4 left-4 flex flex-col space-y-2">
                          <span
                          className={`px-3 py-1 rounded-full text-xs font-semibold ${getStatusColor(
                            event.status
                          )}`}>

                            {event.status.charAt(0).toUpperCase() + event.status.slice(1)}
                          </span>
                          <span className="px-3 py-1 rounded-full glass text-xs font-semibold text-foreground">
                            {event.category}
                          </span>
                        </div>
                      </div>

                      <div className="p-5 sm:p-8 md:p-12 flex flex-col justify-center space-y-4 sm:space-y-6">
                        <div className="space-y-4">
                          <h3 className="text-2xl md:text-3xl font-heading font-bold text-foreground">
                            {event.title}
                          </h3>
                          <p className="text-muted-foreground leading-relaxed">
                            {event.description}
                          </p>
                        </div>

                        <div className="space-y-3">
                          <div className="flex items-center space-x-3 text-foreground">
                            <Icon name="CalendarIcon" size={20} className="text-primary" />
                            <span className="font-medium">{formatDateValue(event.date)}</span>
                          </div>
                          <div className="flex items-center space-x-3 text-foreground">
                            <Icon name="MapPinIcon" size={20} className="text-primary" />
                            <span className="font-medium">{event.location}</span>
                          </div>
                        </div>

                        <Link
                        href={`/events/${event.id}`}
                        className="inline-flex items-center space-x-2 px-6 py-3 rounded-xl font-heading font-semibold bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300 shadow-cta hover:shadow-elevated w-fit">

                          <span>View Event Details</span>
                          <Icon name="ArrowRightIcon" size={18} />
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Navigation Buttons */}
          <button
            onClick={handlePrevious}
            className="absolute left-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass shadow-glass hover:shadow-cta flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            aria-label="Previous event">

            <Icon name="ChevronLeftIcon" size={24} className="text-primary" />
          </button>
          <button
            onClick={handleNext}
            className="absolute right-4 top-1/2 -translate-y-1/2 w-12 h-12 rounded-full glass shadow-glass hover:shadow-cta flex items-center justify-center transition-all duration-300 hover:scale-110 z-10"
            aria-label="Next event">

            <Icon name="ChevronRightIcon" size={24} className="text-primary" />
          </button>

          {/* Dots Indicator */}
          <div className="flex items-center justify-center space-x-2 mt-8">
            {events.map((_, index) =>
            <button
              key={index}
              onClick={() => setCurrentIndex(index)}
              className={`h-2 rounded-full transition-all duration-300 ${
              index === currentIndex ? 'w-8 bg-primary' : 'w-2 bg-muted'}`
              }
              aria-label={`Go to event ${index + 1}`} />

            )}
          </div>
        </div>

        <div className="text-center mt-12">
          <Link
            href="/events"
            className="inline-flex items-center space-x-2 px-8 py-4 rounded-xl font-heading font-semibold text-lg bg-secondary text-secondary-foreground hover:bg-secondary/90 transition-all duration-300 shadow-cta hover:shadow-elevated transform hover:-translate-y-1">

            <span>View All Events</span>
            <Icon name="CalendarIcon" size={20} />
          </Link>
        </div>
      </div>
    </section>);

};

export default EventsCarousel;